// Payout requests: the user's own, the ones a moderator pre-validates, and
// the admin's full list with approvals (one, all, or reject with a note).
import { useMemo, useState } from "react";
import { payoutsApi } from "@/api/payouts.api";
import type { BackendPayoutRequest } from "@/lib/types/payments";
import { sumRequests } from "../logic/earnings-logic";
import {
  apiErrorMessage,
  mergeRequestsNewestFirst,
  moderatorVisibleRequests,
  pendingRequestsOfType,
  requestsOfType,
  totalAmount,
} from "../logic/payout-logic";
import { isAdminOrSuper, type GetPaidBase } from "./get-paid-context";

type BulkTarget = "moderator" | "contributor";
const CLOSED_BULK = { open: false, target: null, count: 0, totalAmount: 0 };

export function usePayoutRequests({ t, user, toast }: GetPaidBase) {
  const [payoutRequests, setPayoutRequests] = useState<BackendPayoutRequest[]>([]);
  const [adminPayoutRequests, setAdminPayoutRequests] = useState<BackendPayoutRequest[]>([]);
  const [myPayoutRequests, setMyPayoutRequests] = useState<BackendPayoutRequest[]>([]);
  const [, setPendingModeratorRequests] = useState<BackendPayoutRequest[]>([]);
  const [, setLoadingRequests] = useState(false);
  const [bulkApproveDialog, setBulkApproveDialog] = useState<{ open: boolean; target: BulkTarget | null; count: number; totalAmount: number }>(CLOSED_BULK);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; requestId: string | null; note: string }>({ open: false, requestId: null, note: "" });

  const pendingPayoutAmount = useMemo(() => sumRequests(myPayoutRequests, ["PENDING", "APPROVED", "PAID"]), [myPayoutRequests]);
  const awaitingPayoutAmount = useMemo(() => sumRequests(myPayoutRequests, ["PENDING", "APPROVED"]), [myPayoutRequests]);
  const hasPendingPayoutRequest = awaitingPayoutAmount > 0;

  const moderatorRequestsToProcess = useMemo(() => requestsOfType(adminPayoutRequests, "MODERATOR"), [adminPayoutRequests]);
  const contributorRequestsToProcess = useMemo(() => requestsOfType(adminPayoutRequests, "USER"), [adminPayoutRequests]);

  const loadMyPayoutRequests = async () => {
    if (!user?.id) return;
    try {
      const res = user.role === "moderator"
        ? await payoutsApi.getModeratorPayoutRequests()
        : await payoutsApi.getUserPayoutRequests();
      setMyPayoutRequests(res?.items || []);
    } catch {
      toast({ title: t.error, description: t.getPaid.myRequestsLoadError, variant: "destructive" });
    }
  };

  const loadPendingPayoutRequests = async () => {
    if (user?.role !== "moderator" && !isAdminOrSuper(user?.role)) return;
    setLoadingRequests(true);
    try {
      const res = await payoutsApi.getPayoutRequests("PENDING");
      if (user?.role === "moderator") {
        setPayoutRequests(moderatorVisibleRequests(res.items));
      } else if (isAdminOrSuper(user?.role)) {
        setPendingModeratorRequests(res.items.filter((r) => r.requestType === "MODERATOR"));
      }
    } catch {
      toast({ title: t.error, description: t.getPaid.payoutRequestsLoadError, variant: "destructive" });
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadAdminPayoutRequests = async () => {
    if (!isAdminOrSuper(user?.role) && user?.role !== "moderator") return;
    setLoadingRequests(true);
    try {
      const [pendingRes, approvedRes, paidRes, rejectedRes] = await Promise.all([
        payoutsApi.getPayoutRequests("PENDING"),
        payoutsApi.getPayoutRequests("APPROVED"),
        payoutsApi.getPayoutRequests("PAID"),
        payoutsApi.getPayoutRequests("REJECTED"),
      ]);
      setAdminPayoutRequests(mergeRequestsNewestFirst([pendingRes.items, approvedRes.items, paidRes.items, rejectedRes.items]));
    } catch {
      toast({ title: t.error, description: t.getPaid.payoutRequestsLoadError, variant: "destructive" });
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleValidatePayoutRequest = async (requestId: string, action: "APPROVED" | "REJECTED", noteOverride?: string) => {
    if (user?.role !== "moderator" && !isAdminOrSuper(user?.role)) return;
    const note = action === "REJECTED" ? noteOverride : undefined;
    try {
      if (isAdminOrSuper(user?.role)) {
        if (action === "APPROVED") {
          await payoutsApi.approvePayoutRequest(Number(requestId));
        } else {
          await payoutsApi.rejectPayoutRequest(Number(requestId), note || undefined);
        }
      } else {
        await payoutsApi.validatePayoutRequest(Number(requestId), { action, note: note || undefined });
      }
      toast({
        title: action === "APPROVED" ? t.getPaid.requestApproved : t.getPaid.requestRejected,
        description: action === "APPROVED" ? t.getPaid.paymentCanProceed : t.getPaid.requesterNotified,
      });
      setPayoutRequests((prev) => prev.filter((r) => r.id !== requestId));
      await loadAdminPayoutRequests();
    } catch (error: any) {
      toast({ title: t.error, description: apiErrorMessage(error, t.getPaid.requestValidateFailed), variant: "destructive" });
    }
  };

  const handleApproveAdminRequest = async (request: BackendPayoutRequest) => {
    if (!isAdminOrSuper(user?.role)) return;
    if (request.status !== "PENDING") return;
    try {
      await payoutsApi.approvePayoutRequest(Number(request.id));
      toast({ title: t.getPaid.requestApproved, description: t.getPaid.requestApprovedDesc.replace("{name}", request.userName) });
      await loadAdminPayoutRequests();
    } catch (error: any) {
      const msg = error?.response?.status === 403 ? t.getPaid.approveForbidden : apiErrorMessage(error, t.getPaid.approveFailed);
      toast({ title: t.error, description: msg, variant: "destructive" });
    }
  };

  const openBulkApprove = (target: BulkTarget, requests: BackendPayoutRequest[]) => {
    if (requests.length === 0) return;
    setBulkApproveDialog({ open: true, target, count: requests.length, totalAmount: totalAmount(requests) });
  };

  const handleApproveAllModeratorRequests = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    openBulkApprove("moderator", pendingRequestsOfType(adminPayoutRequests, "MODERATOR"));
  };

  const handleApproveAllContributorRequests = async () => {
    if (!isAdminOrSuper(user?.role)) return;
    openBulkApprove("contributor", contributorRequestsToProcess.filter((request) => request.status === "PENDING"));
  };

  const executeBulkApprove = async (target: BulkTarget) => {
    if (!isAdminOrSuper(user?.role)) return;
    const requests = pendingRequestsOfType(adminPayoutRequests, target === "moderator" ? "MODERATOR" : "USER");
    if (requests.length === 0) {
      setBulkApproveDialog(CLOSED_BULK);
      return;
    }

    setLoadingRequests(true);
    try {
      const results = await Promise.allSettled(
        requests.map(async (request) => {
          await payoutsApi.approvePayoutRequest(Number(request.id));
          return request.id;
        }),
      );
      const paidIds = results
        .filter((result): result is PromiseFulfilledResult<string> => result.status === "fulfilled")
        .map((result) => result.value);
      const failedCount = results.length - paidIds.length;

      await loadAdminPayoutRequests();

      toast({
        title: target === "moderator" ? t.getPaid.bulkApproveModeratorsDone : t.getPaid.bulkApproveContributorsDone,
        description: t.getPaid.bulkApproveDesc.replace("{count}", String(paidIds.length)).replace("{failed}", String(failedCount)),
        variant: failedCount > 0 ? "destructive" : "default",
      });
    } catch (error: any) {
      const fallback = target === "moderator" ? t.getPaid.approveModeratorsFailed : t.getPaid.approveContributorsFailed;
      toast({ title: t.error, description: apiErrorMessage(error, fallback), variant: "destructive" });
    } finally {
      setLoadingRequests(false);
      setBulkApproveDialog(CLOSED_BULK);
    }
  };

  const openRejectDialog = (requestId: string) => {
    setRejectDialog({ open: true, requestId, note: "" });
  };

  const handleRejectWithDialog = async () => {
    if (!rejectDialog.requestId) return;
    await handleValidatePayoutRequest(rejectDialog.requestId, "REJECTED", rejectDialog.note.trim() || undefined);
    setRejectDialog({ open: false, requestId: null, note: "" });
  };

  return {
    myPayoutRequests, payoutRequests, adminPayoutRequests,
    pendingPayoutAmount, awaitingPayoutAmount, hasPendingPayoutRequest,
    moderatorRequestsToProcess, contributorRequestsToProcess,
    loadMyPayoutRequests, loadPendingPayoutRequests, loadAdminPayoutRequests,
    handleValidatePayoutRequest, handleApproveAdminRequest,
    handleApproveAllModeratorRequests, handleApproveAllContributorRequests,
    executeBulkApprove, bulkApproveDialog, setBulkApproveDialog,
    rejectDialog, setRejectDialog, openRejectDialog, handleRejectWithDialog,
  };
}
